import { useState, useCallback } from "react";
import {
  getListing,
  createListing,
  updateListing,
  deleteListing,
  getListingsByCategory,
  createGuestListing,
  getPaginatedListings as apiGetPaginatedPosts,
  queryListing,
} from "../api/listings";

export const useListings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getPaginatedListings = async (page, limit, token) => {
    setLoading(true);
    try {
      const res = await apiGetPaginatedPosts(page, limit, token);
      setListings(res);
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch listings by category
  const fetchListingsByCategory = useCallback(async (categoryId, location) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getListingsByCategory(categoryId, location);
      setListings(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper to build ListingCreate payload
  const buildListingPayload = (
    values,
    staticFields,
    categoryId,
    postFormFields = []
  ) => {
    const staticFieldNames = staticFields.map((f) => f.label);
    const payload = {};
    const attributes = {};
    for (const key in values) {
      if (staticFieldNames.includes(key)) {
        payload[key] = values[key];
      } else if (key.startsWith("attr_")) {
        const attrLabel = key.replace(/^attr_/, "");
        const fieldDef = postFormFields.find((f) => f.label === attrLabel);
        if (fieldDef) {
          if (fieldDef.type === "checkbox") {
            attributes[attrLabel] = Boolean(values[key]);
          } else if (fieldDef.type === "checkbox_group") {
            attributes[attrLabel] = Array.isArray(values[key])
              ? values[key]
              : [];
          } else if (fieldDef.type === "number") {
            // Convert to number if not null/empty
            const val = values[key];
            attributes[attrLabel] =
              val === "" || val === null || val === undefined
                ? null
                : Number(val);
          } else {
            attributes[attrLabel] = values[key];
          }
        } else {
          attributes[attrLabel] = values[key];
        }
      }
    }
    // Add location fields if present
    if (values.country) payload.country = values.country;
    if (values.city) payload.city = values.city;
    if (values.region) payload.region = values.region;
    if (values.longitude) payload.longitude = values.longitude;
    if (values.latitude) payload.latitude = values.latitude;
    payload["attributes"] = attributes;
    payload["category_id"] = categoryId;
    // Always include guest_email if present
    if (values.guest_email) payload.guest_email = values.guest_email;
    return payload;
  };

  // Add loading/error state for addListing
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);

  const addListing = async (
    formValues,
    staticFields,
    categoryId,
    postFormFields = [],
    token
  ) => {
    setAddLoading(true);
    setAddError(null);
    try {
      const payload = buildListingPayload(
        formValues,
        staticFields,
        categoryId,
        postFormFields
      );
      const newListing = await createListing(payload, token);
      setListings((prev) => [...prev, newListing]);
      setAddLoading(false);
      return newListing;
    } catch (err) {
      setAddError(
        err?.response?.data?.detail || err.message || "Failed to add listing"
      );
      setAddLoading(false);
      throw err;
    }
  };

  const guestAddListing = async (payload) => {
    setAddLoading(true);
    setAddError(null);
    try {
      const newListing = await createGuestListing(payload);
      setAddLoading(false);
      return newListing;
    } catch (err) {
      setAddError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to add guest listing"
      );
      setAddLoading(false);
      throw err;
    }
  };

  const editListing = async (listingId, updatedData, token) => {
    const updated = await updateListing(listingId, updatedData, token);

    return updated;
  };

  const removeListing = async (listingId, token) => {
    await deleteListing(listingId, token);
  };

  const fetchListing = async (listingId) => {
    return await getListing(listingId);
  };

  const fetchListingsBySearch = async (query, page, perPage) => {
    setLoading(true);
    setError(null);
    try {
      const data = await queryListing(query, page, perPage);
      setListings(data.data);
      return data;
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to fetch listings by search"
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    listings,
    loading,
    error,
    fetchListingsByCategory,
    addListing,
    addLoading,
    addError,
    editListing,
    removeListing,
    fetchListing,
    guestAddListing,
    getPaginatedListings,
    fetchListingsBySearch,
  };
};
